import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Footer } from "../components/Footer";

export function SelectClaimingParty({
  handleNext,
  handleBack,
  activeStep,
  isStepOptional,
  steps,
  totalFormData,
  handleDataSubmitted,
}: {
  handleNext: () => void;
  handleBack: () => void;
  activeStep: number;
  isStepOptional: (step: number) => boolean;
  steps: string[];
  totalFormData: Record<string, any>;
  handleDataSubmitted: (data: Record<string, any>, step: string) => void;
}) {
  const [claimingPartyId, setClaimingPartyId] = useState<string>("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const dependents = useMemo(() => {
    return totalFormData?.["identify-policy"]?.dependents || [];
  }, [totalFormData]);

  const policyholder = useMemo(() => {
    return totalFormData?.["identify-policy"]?.policyholder || null;
  }, [totalFormData]);

  const policyholderRiskId: string | undefined = useMemo(() => {
    return totalFormData?.["identify-policy"]?.policyholderRiskId;
  }, [totalFormData]);

  const claimingParties = useMemo(() => {
    const parties: Array<{ id: string; name: string }> = [];
    if (policyholder) {
      const primaryId = policyholderRiskId || policyholder.idNumber;
      if (primaryId) {
        parties.push({
          id: String(primaryId),
          name: policyholder.fullName || "Policyholder",
        });
      }
    }
    for (const d of dependents) {
      const id = d.id || d.dependentId || d.value;
      const name =
        d.name || d.fullName || `${d.firstName || ""} ${d.lastName || ""}`;
      if (id && name)
        parties.push({ id: String(id), name: String(name).trim() });
    }
    return parties;
  }, [policyholder, policyholderRiskId, dependents]);

  useEffect(() => {
    if (totalFormData["select-claiming-party"]) {
      setClaimingPartyId(
        totalFormData["select-claiming-party"].claimingPartyId || ""
      );
    }
  }, [totalFormData]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (claimingParties.length > 0 && !claimingPartyId) {
      errors["claiming-party"] = "Please select a claiming party";
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    handleDataSubmitted({ claimingPartyId }, "select-claiming-party");
    handleNext();
  };

  return (
    <>
      <h4 style={{ borderBottom: "1px solid #ccc", margin: "1em 0" }}>
        Select claiming party
      </h4>
      <form onSubmit={handleSubmit} style={{ paddingBottom: "5em" }}>
        {claimingParties.length === 0 ? (
          <p>No policy members found for this policy.</p>
        ) : (
          <FormControl
            sx={{
              m: { xs: 0, sm: 1 },
              mb: { xs: 1, sm: 1 },
              width: { xs: 300, sm: 600 },
              minWidth: { xs: "100%", sm: "auto" },
            }}
            error={!!formErrors["claiming-party"]}
          >
            <InputLabel id="claiming-party-label">Claiming party</InputLabel>
            <Select
              labelId="claiming-party-label"
              id="claiming-party"
              value={claimingPartyId}
              label="Claiming party"
              onChange={(e) => setClaimingPartyId(e.target.value as string)}
            >
              {claimingParties.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
            {formErrors["claiming-party"] && (
              <FormHelperText>{formErrors["claiming-party"]}</FormHelperText>
            )}
          </FormControl>
        )}

        <Box sx={{ display: "flex", flexDirection: "row", py: 2, mb: "10em" }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1, fontSize: "1.2em" }}
          >
            Back
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          <Button type="submit" sx={{ fontSize: "1.2em" }}>
            Next
          </Button>
        </Box>
      </form>
      <Footer />
    </>
  );
}
